<script setup>
// O BOTÃO DAS PENDÊNCIAS (18/10/2026), ao lado do sininho.
//
// Victor: o advogado "recebe notificação o dia todo, o tempo todo" — medido na A&T, 150 a 300
// não lidas por pessoa. O que espera uma DECISÃO precisava de um lugar próprio, que chame a
// atenção sem obrigar: este botão âmbar só aparece quando há algo esperando, com o número à
// vista, e abre a gaveta onde se decide ali mesmo. O sininho continua sendo o que aconteceu.
//
// O BOM DIA SOBE SOZINHO, UMA VEZ POR DIA (21/10/2026): no primeiro acesso do dia, havendo o que decidir, a gaveta abre
// no "Bom dia" (aprovações no topo) — Victor: as pendências "não despertavam a curiosidade". Uma vez por dia, só com
// pendência que pede decisão, nunca por cima de outro modal, e a pessoa desliga na caixinha do próprio Bom dia ou no
// Perfil. As regras puras moram em pendencias.js (`bomDiaAbreSozinho`).
//
// Atualiza sozinho a cada minuto, ao voltar para a aba e a cada decisão em qualquer tela
// (evento `pendencias:mudou`).
//
// SÓ O ÍCONE E O NÚMERO (22/10/2026, Victor: "poderia ser abreviado também, igual o do sininho e do megafone… para não
// ficar ocupando esse espaço todo"). Era assim só no celular e na barra recolhida; agora em todo lugar. O âmbar continua
// marcando o que pede decisão, e o nome completo fica na dica e no leitor de tela.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useUISettings } from 'dashboard/composables/useUISettings';
import pendenciasAPI from 'dashboard/api/pendencias';
import GavetaDePendencias from './GavetaDePendencias.vue';
import {
  bomDiaAbreSozinho,
  bomDiaJaAvaliado,
  CHAVE_AVISO_VISTO,
  hojeLocal,
  marcarBomDiaAvaliado,
  numeroDoBotao,
} from './pendencias';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const P = 'PENDENCIAS';
// O resumo de prazos (briefing) e o modal da OAB abrem por conta própria logo na entrada: espera um instante e só
// sobe se a tela estiver livre. Se não estiver, fica para o próximo acesso (o "visto de hoje" só grava ao abrir).
const ESPERA_DOS_OUTROS_MODAIS = 2500;
let porTipo = null;
let espera = null;

const total = ref(0);
// Só para o administrador: o total do escritório, quando nada é "dele".
const doEscritorio = ref(0);
const aberta = ref(false);
// Aberta pelo resumo da manhã (fatia 4 B): a gaveta abre com o "Bom dia".
const daManha = ref(false);
let relogio = null;
const route = useRoute();
const router = useRouter();

// O sininho do resumo da manhã leva a `?pendencias=manha` em qualquer tela: abre a gaveta e limpa o endereço
// (sem isso, um F5 reabriria a gaveta que a pessoa acabou de fechar).
watch(
  () => route.query?.pendencias,
  valor => {
    if (!valor) return;
    daManha.value = valor === 'manha';
    aberta.value = true;
    router.replace({ query: { ...route.query, pendencias: undefined } });
  },
  { immediate: true }
);

// Discreto: o administrador não tem nada dele, mas o escritório tem. O NÚMERO DO TOPO É SÓ O QUE É DA PESSOA
// (23/10/2026, Victor: "51 pendências… vai ficar só aumentando?"): discreto, o botão fica sem número — ainda abre
// a gaveta no escritório, mas não cobra de ninguém o que é de outra pessoa.
const discreto = computed(() => !total.value && doEscritorio.value > 0);
const numero = computed(() =>
  numeroDoBotao(discreto.value ? doEscritorio.value : total.value)
);
const quantos = computed(() =>
  discreto.value ? doEscritorio.value : total.value
);

const talvezAbrirOBomDia = () => {
  if (bomDiaJaAvaliado() || !porTipo) return;
  // Preferências ainda não chegaram: o `watch` abaixo tenta de novo quando chegarem.
  if (!Object.keys(uiSettings.value || {}).length) return;
  marcarBomDiaAvaliado(true);
  if (!bomDiaAbreSozinho(uiSettings.value, porTipo)) return;

  espera = setTimeout(() => {
    espera = null;
    if (aberta.value || document.querySelector('.advup-modal-fundo')) return;
    updateUISettings({ [CHAVE_AVISO_VISTO]: hojeLocal() });
    daManha.value = true;
    aberta.value = true;
  }, ESPERA_DOS_OUTROS_MODAIS);
};

watch(uiSettings, talvezAbrirOBomDia);

// O "Seu dia" abriu nesta carga: a linha das decisões já está nele, e dois avisos seguidos, nunca (medido em
// 22/10/2026: fechando o Seu dia antes da espera, o Bom dia abria logo em seguida).
const seuDiaAbriu = () => {
  marcarBomDiaAvaliado(true);
  if (espera) {
    clearTimeout(espera);
    espera = null;
  }
};

const contar = async () => {
  try {
    const { data } = await pendenciasAPI.contagem();
    total.value = data.total || 0;
    doEscritorio.value = data.total_escritorio || 0;
    porTipo = data.por_tipo || {};
    talvezAbrirOBomDia();
  } catch {
    // Sem a contagem, o botão some; nada mais na tela depende dele.
  }
};

const aoVoltar = () => {
  if (document.visibilityState === 'visible') contar();
};

onMounted(() => {
  contar();
  relogio = setInterval(contar, 60000);
  document.addEventListener('visibilitychange', aoVoltar);
  emitter.on('pendencias:mudou', contar);
  emitter.on(BUS_EVENTS.SEU_DIA_ABRIU, seuDiaAbriu);
});

onBeforeUnmount(() => {
  clearInterval(relogio);
  // Outro botão (barra larga ou recolhida) assume a avaliação.
  if (espera) {
    clearTimeout(espera);
    marcarBomDiaAvaliado(false);
  }
  document.removeEventListener('visibilitychange', aoVoltar);
  emitter.off('pendencias:mudou', contar);
  emitter.off(BUS_EVENTS.SEU_DIA_ABRIU, seuDiaAbriu);
});
</script>

<template>
  <button
    v-if="quantos > 0"
    type="button"
    class="pend-botao"
    :class="{ 'pend-botao--discreto': discreto }"
    :title="
      discreto
        ? t(`${P}.BOTAO_DICA_ESCRITORIO`, { n: quantos }, quantos)
        : t(`${P}.BOTAO_DICA`, { n: quantos }, quantos)
    "
    :aria-label="
      discreto
        ? t(`${P}.BOTAO_DICA_ESCRITORIO`, { n: quantos }, quantos)
        : t(`${P}.BOTAO_DICA`, { n: quantos }, quantos)
    "
    @click="aberta = true"
  >
    <i class="i-ph-hand-pointing-fill pend-botao__ico" aria-hidden="true" />
    <span v-if="!discreto" class="pend-botao__n">{{ numero }}</span>
  </button>
  <GavetaDePendencias
    :aberta="aberta"
    :escopo-inicial="discreto && !daManha ? 'escritorio' : 'meus'"
    :da-manha="daManha"
    @fechar="
      aberta = false;
      daManha = false;
    "
    @mudou="contar"
  />
</template>

<style scoped>
/* Âmbar: "perto, pede você" — a cor da casa para o que se aproxima (skill advup-design §3). */
.pend-botao {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 6px 0 8px;
  border-radius: var(--r-redondo, 999px);
  background: rgb(var(--amber-9));
  color: rgb(var(--amber-12));
  font-size: var(--txt-nota);
  font-weight: var(--peso-titulo);
  transition:
    filter var(--mov-1) var(--mov-curva),
    transform var(--mov-1) var(--mov-curva);
}

.pend-botao:hover {
  filter: brightness(1.05);
}

.pend-botao:focus-visible {
  outline: 2px solid rgb(var(--amber-11));
  outline-offset: 2px;
}

.pend-botao--discreto {
  background: transparent;
  color: rgb(var(--amber-11));
  box-shadow: inset 0 0 0 1px rgb(var(--amber-8));
}

.pend-botao--discreto .pend-botao__n {
  background: rgb(var(--amber-9));
  color: rgb(var(--amber-12));
}

.pend-botao__ico {
  width: 16px;
  height: 16px;
}

.pend-botao__n {
  display: grid;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--r-redondo, 999px);
  background: rgb(var(--amber-12));
  color: rgb(var(--amber-3));
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  line-height: 20px;
  text-align: center;
}
</style>
